import styles from './page-erfa-request.module.scss';
import { FC, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import AppLayout from '../../components/layout/app-layout/app-layout';
import BackButton from '../../components/shared/BackButton/BackButton';
import BtButton from '../bt-button/bt-button';
import Collapsible from '../../components/shared/Collapsible';
import Editor from '../../components/shared/Editor/Editor';
import TitleWrapper from '../../components/shared/TitleWrapper/TitleWrapper';
import DraggableDropdown from '../../components/shared/DraggableDropdown/draggable-dropdown';
import AddCircle from '@material-ui/icons/AddCircle';
import { OptionType } from '../../models/global.model';
import { ServiceErfa } from '../../services/service-erfa';
import { ServiceUser } from '../../services/service-user';
import { TrashIcon } from '../../components/svgs';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import AddRecipientDialog from '../../components/shared/AddRecipientDialog/AddRecipientDialog';
import { UserOptionType } from '../../components/shared/UserSelector/UserSelector';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import { NotificationManager } from "react-notifications";
import EmailPreview from '../../components/shared/EmailPreview/EmailPreview';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { EvaluationErrors, EvaluationLimits } from '../../util/evaluationContract';

type Params = {
  areaSlug: string;
  project: string;
  version: string;
};

type RouteTypes = RouteComponentProps<Params, Record<string, unknown>>;

type Props = {
  history: RouteTypes['history'];
  match: RouteTypes['match'];
  currentProject: CurrentProjectType;
}
type Area = {
  reward: boolean,
  areaName: string,
  areaId: string,
  supplierId: {
    _id: string,
    name: string
  }
};
type CurrentProjectType = {
  area: {
    areaId: {
      _id: string,
      name: string
    }
    suppliers: Area[]
  }[],
  name: string,
  number?: string,
  slug: string,
}

type ErfaRequestT = TFunction<'projects', 'erfaRequest'>;
type EvaluationsT = TFunction<'evaluations'>;

export type ErfaSubmitResult = {
  sent?: number;
  failed?: unknown[];
  skipped?: { reason?: string }[];
  inProgress?: boolean;
  requestIds?: string[];
};

export type SubmitToast = {
  kind: 'success' | 'warning' | 'error' | 'info';
  message: string;
  leave: boolean;
};

const createIdempotencyKey = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

const isNetworkFailure = (error: unknown) =>
  typeof error === 'object' && error !== null && !('response' in error && (error as { response?: unknown }).response);

const errorKeyOf = (error: unknown) =>
  (error as { response?: { data?: { error?: unknown } } } | null)?.response?.data?.error;

export type NetworkRetry = { key: string; payload: string };

export const idempotencyKeyFor = (retry: NetworkRetry | null, payload: string) =>
  retry?.payload === payload ? retry.key : createIdempotencyKey();

export const networkRetryAfter = (error: unknown, key: string, payload: string): NetworkRetry | null =>
  isNetworkFailure(error) ? { key, payload } : null;

export const describeSubmitResult = (
  result: ErfaSubmitResult | undefined,
  t: ErfaRequestT,
  tEvaluations: EvaluationsT,
): SubmitToast => {
  if (typeof result?.sent !== 'number') {
    return { kind: 'success', message: t('notification.success.submit', 'Skickat'), leave: true };
  }
  if (result.inProgress) {
    return {
      kind: 'info',
      message: tEvaluations('toast.inProgress', 'Utvärderingarna skickas fortfarande. Se status om en stund.'),
      leave: true,
    };
  }
  const failed = result.failed?.length ?? 0;
  const skipped = result.skipped?.length ?? 0;
  const skippedSuppliers = result.skipped?.filter(skip => skip.reason === EvaluationErrors.SupplierNotEvaluable).length ?? 0;
  const skippedRecipients = skipped - skippedSuppliers;
  if (result.sent === 0 && failed > 0) {
    return { kind: 'error', message: tEvaluations('toast.nothingSent', 'Inget kunde skickas. Försök igen senare.'), leave: false };
  }
  const created = (result.requestIds?.length ?? 0) > 0;
  const skippedSuppliersMessage = tEvaluations('toast.skippedSuppliers', {
    count: skippedSuppliers,
    defaultValue_one: '{{count}} leverantör kunde inte utvärderas.',
    defaultValue_other: '{{count}} leverantörer kunde inte utvärderas.',
  });
  if (result.sent === 0 && !created) {
    return {
      kind: 'warning',
      message: skippedSuppliers > 0 && skippedRecipients === 0
        ? skippedSuppliersMessage
        : tEvaluations(
          'toast.allSkipped',
          'Inget skickades. Alla mottagare hoppades över, till exempel för att de redan har en öppen utvärdering av leverantören.',
        ),
      leave: false,
    };
  }
  const message = [
    result.sent === 0
      ? tEvaluations('toast.createdNoEmail', 'Utvärderingen är skapad. Inget mejl skickades.')
      : tEvaluations('toast.sent', {
        count: result.sent,
        defaultValue_one: 'En inbjudan till utvärdering skickades.',
        defaultValue_other: '{{count}} inbjudningar till utvärdering skickades.',
      }),
    failed > 0 && tEvaluations('toast.failed', {
      count: failed,
      defaultValue_one: '{{count}} kunde inte skickas.',
      defaultValue_other: '{{count}} kunde inte skickas.',
    }),
    skippedRecipients > 0 && tEvaluations('toast.skipped', {
      count: skippedRecipients,
      defaultValue_one: '{{count}} mottagare hoppades över.',
      defaultValue_other: '{{count}} mottagare hoppades över.',
    }),
    skippedSuppliers > 0 && skippedSuppliersMessage,
  ].filter(Boolean).join(' ');
  return { kind: 'success', message, leave: true };
};

export const getValidationMessage = (areas: string[], recipients: UserOptionType[], questions: OptionType[], t: TFunction<'projects', 'erfaRequest'>) => {
  const validationMessagesArray = [];
  if (areas?.length === 0) {
    validationMessagesArray.push(t('validation.areas', 'minst en leverantör att utvärdera'));
  }
  if (recipients?.length === 0) {
    validationMessagesArray.push(t('validation.recipients', 'minst en mottagare'));
  }
  if (questions?.length === 0) {
    validationMessagesArray.push(t('validation.questions', 'minst en fråga'));
  }
  const questionCounts = questions.map(o => o?.label.toLowerCase().trim() ?? "").reduce((map, key) => map.set(key, (map.get(key) ?? 0) + 1), new Map<string, number>());
  const duplicateQuestions = Array.from(questionCounts.values()).find(count => count > 1);
  const emptyQuestions = questionCounts.get("") ?? 0;

  const validationMessage = [
    validationMessagesArray.length ? t('validation.emptyFields', 'Välj {{validationMessages_list}}.', { validationMessages: validationMessagesArray }) : null,
    duplicateQuestions ? t('validation.duplicateQuestions', 'Samma fråga finns med flera gånger.') : null,
    emptyQuestions > 0 ? t('validation.emptyQuestions', 'Flera frågor är tomma.', { count: emptyQuestions }) : null,
  ].filter(x => x).join(' ');

  return validationMessage;
}

export const getRecipientCapMessage = (recipients: UserOptionType[], t: EvaluationsT) =>
  recipients.length > EvaluationLimits.MaxRecipientsPerRequest
    ? t('validation.tooManyRecipients', 'Välj högst {{max}} mottagare.', { max: EvaluationLimits.MaxRecipientsPerRequest })
    : '';

const MAX_PICKED_QUESTIONS = EvaluationLimits.MaxQuestionsPerRequest - 1;

export const getQuestionCapMessage = (questions: OptionType[], t: EvaluationsT) =>
  questions.length > MAX_PICKED_QUESTIONS
    ? t('validation.tooManyQuestions', 'Välj högst {{max}} frågor.', { max: MAX_PICKED_QUESTIONS })
    : '';

const PageErfaRequest: FC<Props> = ({history, match, currentProject}) => {
  const [t] = useTranslation('projects', { keyPrefix: 'erfaRequest' });
  const [tEvaluations] = useTranslation('evaluations');
  const [questions, setQuestions] = useState<OptionType[]>([]);
  const [questionsOpen, setQuestionsOpen] = useState(true);
  const [questionOptions, setQuestionOptions] = useState<OptionType[]>([]);
  const [recipientsOpen, setRecipientsOpen] = useState(true);
  const [recipients, setRecipients] = useState<(UserOptionType[])>([]);
  const [userOptions, setUserOptions] = useState<UserOptionType[]>([]);
  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [addRecipientDialogKey, setAddRecipientDialogKey] = useState(0);
  const [areAreasOpen, setAreAreasOpen] = useState(true);
  const [areas, setAreas] = useState<Area[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [isEmailOpen, setIsEmailOpen] = useState(true);
  const [emailText, setEmailText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const networkRetry = useRef<NetworkRetry | null>(null);

  const fetchQuestions = async () => {
    const options = await ServiceErfa.getQuestions();
    setQuestionOptions(options.map((o: {_id: string, question: string }) => ({value: o._id, label: o.question})));
  };

  const fetchUsers = async () => {
    const roles = await ServiceUser.getUsersWithRoles();
    const users = roles.length && roles.reduce((arr : UserOptionType[], elem : {users : UserOptionType[]}) => [...arr, ...elem.users], []);
    const people = users.map((user: UserOptionType) => ({ ...user, label: user.name, value: user._id }));
    setUserOptions(people);
  }

  useEffect(() => {
    (async () => {
      await fetchQuestions();
      await fetchUsers();
    })();
  }, [])

  useEffect(() => {
    const temp: Area[] = [];
    currentProject.area.forEach(area => area.suppliers.forEach(supplier => {
      if (supplier.reward) temp.push({...supplier, areaId: area.areaId._id, areaName: area.areaId.name});
    }));
    setAreas(temp);
  }, [currentProject])

  const goBack = () => {
    const url = match.url.split('/');
    url.splice(4);
    history.push(url.join('/'));
  };

  const leaveAfter = (result: ErfaSubmitResult) => {
    if (result?.requestIds?.length === 1 && (result.sent ?? 0) > 0) {
      const orgSlug = match.url.split('/')[1];
      history.push(`/${orgSlug}/supplier-database/evaluations/requests/${result.requestIds[0]}`);
      return;
    }
    goBack();
  };

  const renderSuppliers = () => {
    const isAreaSelected = (id: string) => selectedAreas.includes(id);

    return <Collapsible
      header={<strong>{t('suppliers.heading', 'Välj leverantörer som ska utvärderas')}</strong>}
      isOpen={areAreasOpen}
      onChangeCollapse={() => setAreAreasOpen(oldState => !oldState)}
    >
      <div className={styles.supplierList}>
        {areas.map(area => area.areaId &&
          <label key={area.areaId}>
            <StyledCheckbox
              checked={isAreaSelected(area.areaId)}
              onChange={() => {
                setSelectedAreas(oldState => {
                  let temp = [...oldState];
                  const supplierId = area.areaId;
                  if (temp.includes(supplierId)) {
                    temp = temp.filter(id => id !== supplierId)
                  } else {
                    temp.push(supplierId);
                  }
                  return temp;
                })
              }}
            />
            <div>
              <span>{area.areaName}</span>
              <span>{area.supplierId.name}</span>
            </div>
          </label>
        )}
      </div>
    </Collapsible>;
  };

  const renderRecipients = () => {
    const onAddRecipient = (user: UserOptionType) => {
      setRecipients(prev => [...prev, user]);
    }
    const removeRecipient = (user: UserOptionType) => {
      setRecipients(prev =>  prev.filter(x=>x._id != user._id));
    }
    const showAddRecipientModal = () => {
      setAddRecipientDialogKey(prev => prev + 1);
      setShowAddRecipient(true);
    }
    return <Collapsible
      header={<strong>{t('recipients.heading', '{{count}} mottagare', { count: recipients?.length ?? 0 })}</strong>}
      isOpen={recipientsOpen}
      onChangeCollapse={() => setRecipientsOpen(prev => !prev)}
      contentContainerClassName={styles.recipients}
    >
      {recipients?.map(recipient =>
        <div key={recipient.email} className={styles.recipient}>
          <div>
            <strong>{recipient.name}</strong><br/>
            <span>{recipient.email}</span>
          </div>
          <div>
            <BtButton color="white" size="xxs" onClick={() => removeRecipient(recipient)}>
              <TrashIcon size="16px" />
            </BtButton>
          </div>
        </div>
      )}
      <BtButton class='white' size='xxs' onClick={showAddRecipientModal}>
        <AddCircle style={{ fontSize: '16px', fill: 'var(--orange-500)' }} />
        <span>{t('recipients.button.add', 'Lägg till mottagare')}</span>
      </BtButton>
      <AddRecipientDialog
        show={showAddRecipient}
        setShow={setShowAddRecipient}
        recipients={recipients}
        userOptions={userOptions}
        onSave={onAddRecipient}
        key={addRecipientDialogKey}
      />
    </Collapsible>;
  }

  const renderQuestions = () => {
    const list = questions;
    const addField = (
      _list: OptionType[],
      setList: (value: SetStateAction<OptionType[]>) => void
    ) => {
      setList((prevState: OptionType[]) => [...prevState, { label: '', value: Date.now().toString() }]);
    };

    return <Collapsible
      header={<strong>{t('questions.heading', 'Utvärdering')}</strong>}
      isOpen={questionsOpen}
      onChangeCollapse={() => setQuestionsOpen(prev => !prev)}
      contentContainerClassName={styles.questions}
    >
      <TitleWrapper title={t('questions.label', 'VÄLJ FRÅGOR')} className={styles.titleWrapper}>
        <div style={{ position: 'relative' }}>
          <div style={{ marginLeft: '-32px', marginRight: '-32px' }}>
            <DraggableDropdown
              options={questionOptions}
              list={list}
              onChange={setQuestions}
            />
          </div>

          <BtButton class='white' size='xxs' onClick={() => addField(list, setQuestions)}>
            <AddCircle style={{ fontSize: '16px', fill: 'var(--orange-500)' }} />
            <span>{t('questions.button.add', 'Lägg till fråga')}</span>
          </BtButton>
        </div>
      </TitleWrapper>
    </Collapsible>;
  }

  const renderEmailViewer = () => {
    const projectName = currentProject.name;

    return <Collapsible
      header={<strong>{t('email.heading', 'E-postmeddelande')}</strong>}
      isOpen={isEmailOpen}
      onChangeCollapse={() => setIsEmailOpen(oldState => !oldState)}
    >
      <div className={styles.emailContainer}>
        <EmailPreview
          type="erfa"
          params={{
            projectName,
            projectNumber: currentProject.number,
            areaName: '<yrkeskategori>',
            supplierName: '<leverantör>',
          }}
        >
          <Editor value={emailText} onChange={setEmailText} wrapperStyle={{ height: '400px' }} />
        </EmailPreview>
      </div>
    </Collapsible>;
  }

  const validationMessage = [
    getValidationMessage(selectedAreas, recipients, questions, t),
    getRecipientCapMessage(recipients, tEvaluations),
    getQuestionCapMessage(questions, tEvaluations),
  ].filter(Boolean).join(' ');

  const handleSubmit = async () => {
    setSubmitting(true);
    const suppliers = selectedAreas.map(selectedAreaId => areas.find(area => area.areaId === selectedAreaId));

    const _questions = questions.map(q => {
      const existing = questionOptions.find(o => o?.label === q?.label);
      return {
        value: existing?.value ?? "new",
        label: q?.label,
        __isNew__: !existing,
      };
    });

    const erfaObj = {
      questions: _questions,
      users: recipients,
      suppliers: suppliers,
      projectSlug: currentProject.slug,
      emailText,
    }
    const payload = JSON.stringify(erfaObj);
    const idempotencyKey = idempotencyKeyFor(networkRetry.current, payload);

    let result: ErfaSubmitResult;
    try {
      result = await ServiceErfa.submitErfa({ ...erfaObj, idempotencyKey });
    } catch (error) {
      networkRetry.current = networkRetryAfter(error, idempotencyKey, payload);
      NotificationManager.error(
        errorKeyOf(error) === EvaluationErrors.MessageTooLong
          ? tEvaluations('toast.messageTooLong', 'Meddelandet är för långt. Korta det, eller klistra in det som ren text, och försök igen.')
          : tEvaluations('toast.error', 'Det gick inte att skicka utvärderingen. Försök igen.'),
      );
      return;
    } finally {
      setSubmitting(false);
    }
    networkRetry.current = null;
    const toast = describeSubmitResult(result, t, tEvaluations);
    NotificationManager[toast.kind](toast.message);
    if (toast.leave) leaveAfter(result);
  }

  const breadcrumb = useMemo(() => {
    return [currentProject.name, t('pageTitle', 'Erfarenhetsåterföring')];
  }, [t, currentProject]);

  return (
    <AppLayout title={
      <BackButton
        onClick={goBack}
        path={breadcrumb}
      />
    }>
      <div className={styles.container}>
        <div className={styles.content}>
          {renderSuppliers()}
          {renderRecipients()}
          {renderQuestions()}
          {renderEmailViewer()}
          <TooltipContainer
            renderReferenceComponent={(className, ref) => (
              <div style={{ alignSelf: 'flex-end' }} ref={ref} className={className}>
                <BtButton
                  loaderShow={submitting}
                  onClick={handleSubmit}
                  disabled={!!validationMessage}
                >
                  {t('button.submit', 'Skicka')}
                </BtButton>
              </div>
          )}>
            {validationMessage && (
              <span style={{ maxWidth: '310px', width: 'max-content' }}>{validationMessage}</span>
            )}
          </TooltipContainer>
        </div>
      </div>
    </AppLayout>
  );
}

export default PageErfaRequest;
